import * as React from 'react';
import { StatesButton, StatesButtonProps, StatesButtonStates } from '.';
import {
  StatesButtonDriver,
  statesButtonDriverFactory,
} from './StatesButton.driver';
import { createRendererWithUniDriver } from '../../test/utils';
import { act } from '@testing-library/react';

jest.useFakeTimers();

describe('StatesButton', () => {
  const render = createRendererWithUniDriver<StatesButtonDriver>(
    statesButtonDriverFactory,
  );

  let onClickSpy;
  let defaultProps: StatesButtonProps;

  beforeEach(() => {
    onClickSpy = jest.fn();

    defaultProps = {
      'data-hook': 'data-hook',
      state: StatesButtonStates.IDLE,
      idleContent: 'idle',
      inProgressContent: 'loading',
      successContent: 'success',
      failureContent: 'failure',
      onClick: onClickSpy,
      disabled: false,
    };
  });

  describe('default props', () => {
    let driver: StatesButtonDriver;
    let ref: React.RefObject<StatesButton>;

    beforeEach(() => {
      ref = React.createRef();
      driver = render(<StatesButton {...defaultProps} ref={ref} />).driver;
    });

    it('should render text from idleContent prop', async () => {
      expect(await driver.getButtonTextContent()).toEqual('idle');
    });

    it('should have aria-live attribute and not aria-busy', async () => {
      expect(await driver.getAttribute('aria-live')).toEqual('assertive');
      expect(await driver.getAttribute('aria-busy')).toBeNull();
    });

    it('should focus on button using ref', async () => {
      expect(await driver.isFocus()).toEqual(false);
      await act(async () => ref.current.focus());
      expect(await driver.isFocus()).toEqual(true);
    });
  });

  describe('states', () => {
    let driver: StatesButtonDriver;

    describe('in progress', () => {
      beforeEach(() => {
        onClickSpy = jest.fn();
        driver = render(
          <StatesButton
            {...defaultProps}
            state={StatesButtonStates.IN_PROGRESS}
            onClick={onClickSpy}
          />,
        ).driver;
      });

      it('should render in progress content', async () => {
        expect(await driver.getButtonTextContent()).toEqual('loading');
      });

      it('should not invoke on click', async () => {
        await driver.click();
        expect(onClickSpy).not.toHaveBeenCalled();
      });

      it('should have aria-busy attribute and not aria-live', async () => {
        expect(await driver.getAttribute('aria-busy')).toEqual('true');
        expect(await driver.getAttribute('aria-live')).toEqual('assertive');
      });
    });

    describe('success', () => {
      let onNotificationEndSpy;
      beforeEach(() => {
        onNotificationEndSpy = jest.fn();
        onClickSpy = jest.fn();
        driver = render(
          <StatesButton
            {...defaultProps}
            state={StatesButtonStates.SUCCESS}
            onClick={onClickSpy}
            onNotificationEnd={onNotificationEndSpy}
          />,
        ).driver;
      });

      it('should render success content when given as prop', async () => {
        expect(await driver.getButtonTextContent()).toEqual('success');
      });

      it('should render check mark when no success content given', async () => {
        driver = render(
          <StatesButton
            {...defaultProps}
            successContent={undefined}
            state={StatesButtonStates.SUCCESS}
            onClick={onClickSpy}
          />,
        ).driver;
        expect(await driver.checkIconExists()).toEqual(true);
      });

      it('should not invoke on click', async () => {
        await driver.click();
        expect(onClickSpy).not.toHaveBeenCalled();
      });

      it('should have aria-live attribute and not aria-busy', async () => {
        expect(await driver.getAttribute('aria-live')).toEqual('assertive');
        expect(await driver.getAttribute('aria-busy')).toBeNull();
      });
    });
  });

  it('when disabled prop is true button should be disabled', async () => {
    const { driver } = render(<StatesButton {...defaultProps} disabled />);
    expect(await driver.isButtonDisabled()).toEqual(true);
  });

  it('when disabled prop is false button should be not disabled', async () => {
    const { driver } = render(
      <StatesButton {...defaultProps} disabled={false} />,
    );
    expect(await driver.isButtonDisabled()).toEqual(false);
  });
});
